import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

interface FallbackProps {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}

function ErrorFallback({ error, resetError }: FallbackProps) {
  const errorMessage = error instanceof Error ? error.message : "Une erreur inconnue s'est produite";
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md text-center space-y-6">
        <div className="flex justify-center">
          <div className="rounded-full bg-destructive/10 p-4">
            <AlertTriangle className="h-12 w-12 text-destructive" />
          </div>
        </div>
        
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            Oups, une erreur est survenue
          </h1>
          <p className="text-muted-foreground">
            Notre équipe a été notifiée et travaille à résoudre le problème.
          </p>
        </div>

        {import.meta.env.DEV && (
          <div className="bg-muted rounded-lg p-4 text-left">
            <p className="text-sm font-mono text-destructive break-all">
              {errorMessage}
            </p>
          </div>
        )}

        <div className="flex gap-3 justify-center">
          <Button onClick={resetError} variant="default" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Réessayer
          </Button>
          <Button 
            variant="outline" 
            onClick={() => window.location.href = "/"}
            className="gap-2"
          >
            <Home className="h-4 w-4" />
            Accueil
          </Button>
        </div>
      </div>
    </div>
  );
}

// Create the Sentry error boundary wrapper
const SentryErrorBoundaryWrapper = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

export const SentryErrorBoundary = Sentry.withErrorBoundary(SentryErrorBoundaryWrapper, {
  fallback: (props) => <ErrorFallback {...props} />,
  showDialog: false, // Disable Sentry's built-in dialog
});

export default SentryErrorBoundary;
